import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Save, Trash2, Loader2, Info, X } from "lucide-react";

/**
 * Iter49 (Item #10) — Styles Master.
 *
 * Admin can add, edit, activate/deactivate, reorder, and delete styles used
 * for house plans. Each style has a unique 2-4 letter Prefix that drives
 * Project ID generation (DH-<PREFIX>-NNNNN).
 *
 * Delete is soft (deactivate) when plans reference the style — keeps
 * historical Project IDs valid.
 */

const emptyDraft = { name: "", prefix: "", active: true, display_order: 100 };

const AdminStyles = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState({}); // id → { field: value }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/styles");
      setItems(data.items || []);
    } catch (e) { toast.error("Failed to load styles", { description: e.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createStyle = async () => {
    if (!draft.name.trim() || !draft.prefix.trim()) {
      toast.error("Name and prefix required"); return;
    }
    setSaving(true);
    try {
      await api.post("/admin/styles", {
        name: draft.name.trim(),
        prefix: draft.prefix.trim().toUpperCase(),
        active: draft.active,
        display_order: Number(draft.display_order) || 100,
      });
      toast.success(`Style "${draft.name}" added`);
      setDraft(emptyDraft);
      await load();
    } catch (e) {
      toast.error("Add failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const updateStyle = async (row, patch) => {
    try {
      await api.patch(`/admin/styles/${row.id}`, patch);
      toast.success("Saved");
      setEditing((prev) => { const { [row.id]: _, ...rest } = prev; return rest; });
      await load();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    }
  };

  const removeStyle = async (row) => {
    if (!window.confirm(`Delete style "${row.name}"?\nIf plans use it, it will be deactivated (not deleted) to preserve historical Project IDs.`)) return;
    try {
      const { data } = await api.delete(`/admin/styles/${row.id}`);
      if (data.soft_deactivated) toast.info(`"${row.name}" deactivated (${data.in_use} plan(s) still use it)`);
      else toast.success(`Deleted "${row.name}"`);
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <AdminLayout title="Styles Master">
      <div className="max-w-5xl space-y-8">
        <div className="border border-border bg-accent/5 p-4 flex items-start gap-3" data-testid="styles-help">
          <Info size={16} className="text-accent shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            Styles power the Plan style dropdown and drive Project ID generation
            (<code>DH-&lt;PREFIX&gt;-NNNNN</code>). Prefixes must be 2-4 letters and unique.
            A prefix cannot be changed once a plan uses that style — this would break existing Project IDs.
          </p>
        </div>

        {/* Add new */}
        <div className="border border-border bg-card p-6" data-testid="add-style-panel">
          <div className="overline text-accent mb-4">Add a new style</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_1fr_auto] gap-3 items-end">
            <div>
              <div className="overline text-muted-foreground mb-2">Style name</div>
              <Input data-testid="new-style-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Bungalow" className="rounded-none h-11" />
            </div>
            <div>
              <div className="overline text-muted-foreground mb-2">Prefix</div>
              <Input data-testid="new-style-prefix" value={draft.prefix} onChange={(e) => setDraft({ ...draft, prefix: e.target.value.toUpperCase().slice(0, 4) })} placeholder="BG" className="rounded-none h-11 font-mono uppercase" />
            </div>
            <div>
              <div className="overline text-muted-foreground mb-2">Display order</div>
              <Input data-testid="new-style-order" type="number" value={draft.display_order} onChange={(e) => setDraft({ ...draft, display_order: e.target.value })} className="rounded-none h-11" />
            </div>
            <label className="flex items-center gap-2 h-11 pt-6" data-testid="new-style-active-row">
              <Checkbox checked={draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: !!v })} data-testid="new-style-active" />
              <span className="text-sm">Active</span>
            </label>
            <Button onClick={createStyle} disabled={saving} className="rounded-none bg-primary hover:bg-primary/90 h-11" data-testid="new-style-save">
              {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Adding…</> : <><Plus size={14} className="mr-2" /> Add</>}
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="border border-border bg-card" data-testid="styles-table">
          <div className="hidden md:grid grid-cols-[1fr_120px_120px_120px_150px] gap-3 px-6 py-3 border-b border-border bg-background text-[11px] uppercase tracking-widest text-muted-foreground">
            <div>Name</div><div>Prefix</div><div>Order</div><div>Active</div><div></div>
          </div>
          {loading && <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>}
          {!loading && items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm" data-testid="styles-empty">No styles configured yet.</div>}
          {!loading && items.map((s) => {
            const edit = editing[s.id] || {};
            const dirty = Object.keys(edit).length > 0;
            const val = (k) => (edit[k] !== undefined ? edit[k] : s[k]);
            const set = (k, v) => setEditing((prev) => ({ ...prev, [s.id]: { ...(prev[s.id] || {}), [k]: v } }));
            return (
              <div key={s.id} className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_120px_150px] gap-3 items-center px-6 py-3 border-b border-border last:border-b-0" data-testid={`style-row-${s.id}`}>
                <Input
                  data-testid={`style-name-${s.id}`}
                  value={val("name")}
                  onChange={(e) => set("name", e.target.value)}
                  className="rounded-none h-10"
                />
                <Input
                  data-testid={`style-prefix-${s.id}`}
                  value={val("prefix")}
                  onChange={(e) => set("prefix", e.target.value.toUpperCase().slice(0, 4))}
                  className="rounded-none h-10 font-mono uppercase"
                />
                <Input
                  data-testid={`style-order-${s.id}`}
                  type="number"
                  value={val("display_order")}
                  onChange={(e) => set("display_order", Number(e.target.value))}
                  className="rounded-none h-10"
                />
                <label className="flex items-center gap-2">
                  <Checkbox
                    data-testid={`style-active-${s.id}`}
                    checked={!!val("active")}
                    onCheckedChange={(v) => set("active", !!v)}
                  />
                  <span className="text-sm">{val("active") ? "Active" : "Inactive"}</span>
                </label>
                <div className="flex gap-2 justify-end">
                  {dirty && (
                    <Button
                      onClick={() => updateStyle(s, edit)}
                      className="rounded-none h-9 bg-primary"
                      data-testid={`style-save-${s.id}`}
                    ><Save size={12} className="mr-1" /> Save</Button>
                  )}
                  {dirty ? (
                    <Button
                      onClick={() => setEditing((prev) => { const { [s.id]: _, ...rest } = prev; return rest; })}
                      variant="outline" className="rounded-none h-9"
                      data-testid={`style-cancel-${s.id}`}
                    ><X size={12} /></Button>
                  ) : (
                    <Button
                      onClick={() => removeStyle(s)}
                      variant="outline" className="rounded-none h-9 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      data-testid={`style-delete-${s.id}`}
                    ><Trash2 size={12} /></Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminStyles;
